import { User } from "../types/schema";
import { getApiClient } from './api-client';

export interface UpdateUserRequest {
  firstName?: string;
  lastName?: string;
  username?: string;
}

/**
 * User Service
 * Handles all user-related API calls
 */
class UserService {
  /**
   * Get all users in the workspace
   * @param workspaceId Workspace ID (optional for backward compatibility)
   * @returns List of users
   */
  async getWorkspaceUsers(workspaceId: number): Promise<User[]> {
    if (!workspaceId) {
      console.warn('getWorkspaceUsers called without workspaceId - returning empty array');
      return [];
    }

    const client = getApiClient();
    // Always use workspace-scoped endpoint for security
    return client.get<User[]>(`/workspaces/${workspaceId}/users`);
  }

  /**
   * Search users by keyword (username or email)
   * @param keyword Search keyword
   * @param workspaceId Workspace ID
   * @returns Filtered list of users
   */
  async searchUsers(keyword: string, workspaceId: number): Promise<User[]> {
    const users = await this.getWorkspaceUsers(workspaceId);
    const lowerKeyword = keyword.toLowerCase();
    return users.filter(
      (user) =>
        user.username?.toLowerCase().includes(lowerKeyword) ||
        user.email?.toLowerCase().includes(lowerKeyword)
    );
  }

  /**
   * Get user by ID
   * @param id Opaque application user ID
   * @param workspaceId Workspace ID
   * @returns User
   */
  async getUser(id: string, workspaceId: number): Promise<User | null> {
    const users = await this.getWorkspaceUsers(workspaceId);
    return users.find((u) => u.id.toString() === id) || null;
  }

  /**
   * Get current authenticated user
   * @returns Current user
   */
  async getCurrentUser(): Promise<User> {
    const client = getApiClient();
    return client.get<User>('/users/me');
  }

  /**
   * Update current user
   * @param data Update data
   * @returns Updated user
   */
  async updateCurrentUser(data: UpdateUserRequest): Promise<User> {
    const client = getApiClient();
    return client.patch<User>('/users/me', data);
  }
}

// Export singleton instance
export const userService = new UserService();
